/**
 * /genies/:id/distribute — "Get your genie out there" distribution one-pager
 * (configurator direction). Aggregates everything under Distribution into ONE
 * page instead of four separate modals: a hero share-link bar, channel cards
 * (Phone / Website embed / QR code / Email signature / Notifications) each with a
 * status pill + one-tap action, and the Activation pack as a launch-kit banner.
 *
 * REUSES the REAL DistributionCard modals (PhoneNumberModal / WebEmbedModal /
 * QRCodeModal / GenieShareDrawer) for the actions, and the foundation atoms +
 * Distribute* co-located blocks for the on-brand one-pager LAYOUT. Genie data via
 * the REAL useAgent(id). Brand law: genie-* tokens + signature gradient only.
 */
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useAgent, useUpdateAgent } from "@/hooks/agent/useAgents";
import { QRCodeModal, WebEmbedModal } from "@/components/agent";
import GenieShareDrawer from "@/components/agent/drawers/GenieShareDrawer";
import { PhoneNumberModal } from "@/components/phone";
import { ImmersiveBackBar } from "@/components/voice-genie/ImmersiveBackBar";
import { VoiceMascot } from "@/components/voice-genie/VoiceMascot";
import { mascotForAgent } from "@/components/voice-genie/GenieOwnerDetail";
import { resolveGenieAvatarUrl } from "@/utils/image/genieAvatars";
import { QrGlyph } from "@/components/voice-genie/atoms";
import { useAuth } from "@/auth/AuthProvider";
import { buildGenieKeepUrl, buildGenieTalkUrl } from "@/utils/agent/genieShareLinks";
import {
  DistributeActivationPack,
  DistributeAppPreview,
  DistributeChannelCard,
  DistributeEmbedPreview,
  DistributeNotificationsRow,
  DistributePhonePreview,
  DistributeShareBar,
  DistributeSignaturePreview,
} from "./DistributeChannels";

type DistributeModal = "phone" | "webEmbed" | "qr" | "share";

const DistributePage = () => {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const { data: agent, isLoading, isError, error } = useAgent(id);

  const updateAgent = useUpdateAgent();
  const [activeModal, setActiveModal] = useState<DistributeModal | null>(null);
  const [copied, setCopied] = useState(false);

  const urlName = agent?.url_name ?? agent?.page?.url_name ?? null;
  const origin = typeof window !== "undefined" ? window.location.origin : "https://helpgenie.ai";
  const shareUrl = urlName ? `${origin}/live-agent/${urlName}` : null;
  const shareLabel = urlName ? `helpgenie.ai/${urlName}` : "Finish setup to get your link";
  // Carries the app's receive intent, so a link opened on a phone with Help
  // Genie installed also saves the genie into My Genies.
  const talkUrl = buildGenieTalkUrl(urlName, { receiveIntent: true, origin });
  // App-first link — see utils/agent/genieShareLinks.
  const keepUrl = buildGenieKeepUrl(
    { urlName, agentId: agent?.id, name: agent?.name },
    { origin },
  );

  const phoneNumber = agent?.phone?.number ?? null;
  const ownerName = useMemo(() => {
    const name = userProfile?.full_name || user?.email?.split("@")[0] || "Your team";
    return `${name} · ${agent?.name ?? "Help Genie"}`;
  }, [userProfile?.full_name, user?.email, agent?.name]);

  const close = () => setActiveModal(null);

  const handleCopy = async () => {
    if (!talkUrl) {
      toast.error("Finish setting up your genie to get a shareable link.");
      return;
    }
    try {
      await navigator.clipboard.writeText(talkUrl);
      setCopied(true);
      toast.success("Share link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy the link. Please try again.");
    }
  };

  const handleCopyAppLink = async () => {
    if (!keepUrl) {
      toast.error("Finish setting up your genie to get an app link.");
      return;
    }
    try {
      await navigator.clipboard.writeText(keepUrl);
      toast.success("App link copied — it saves this genie into their Help Genie app");
    } catch {
      toast.error("Could not copy the link. Please try again.");
    }
  };

  const handleOpen = () => {
    if (!shareUrl) {
      toast.error("Finish setting up your genie to open its page.");
      return;
    }
    window.open(shareUrl, "_blank", "noopener,noreferrer");
  };

  const handleActivationPack = () => {
    if (!agent) return;
    navigate(`/genie-hub/activation/genie/${agent.id}`);
  };

  /* ── Loading skeleton (matches final layout) ── */
  if (isLoading) {
    return (
      <div className="flex flex-col h-screen w-full bg-genie-background overflow-hidden">
        <ImmersiveBackBar label="My Genie" to={`/genies/${id}/immersive-setup`} />
        <div className="flex-1 min-h-0 overflow-auto">
          <div className="flex flex-col items-center px-6 pt-7 pb-12">
            <div className="w-full max-w-[940px] flex flex-col gap-5 animate-pulse">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-200" />
                <div className="h-4 w-40 rounded bg-gray-200" />
              </div>
              <div className="h-7 w-72 rounded bg-gray-200" />
              <div className="h-20 rounded-2xl bg-gray-200" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="h-40 rounded-2xl bg-gray-200" />
                ))}
              </div>
              <div className="h-16 rounded-2xl bg-gray-200" />
              <div className="h-24 rounded-2xl bg-gray-200" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── Error / not found ── */
  if (isError || !agent) {
    return (
      <div className="flex flex-col h-screen w-full bg-genie-background overflow-hidden">
        <ImmersiveBackBar label="My Genie" to={`/genies/${id}/immersive-setup`} />
        <div className="flex-1 min-h-0 overflow-auto">
          <div className="flex flex-col items-center justify-center px-6 pt-20 text-center">
            <div className="w-full max-w-[460px] bg-white rounded-2xl border border-genie-border shadow-sm p-8">
              <h2 className="text-lg font-bold text-[#222B45]">We couldn&rsquo;t load this genie</h2>
              <p className="text-sm text-gray-500 mt-2">
                {error instanceof Error ? error.message : "Please try again in a moment."}
              </p>
              <button
                type="button"
                onClick={() => navigate("/genies")}
                className="mt-5 h-10 px-5 rounded-2xl text-[13px] font-semibold text-white bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-lg shadow-genie-primary/25 hover:opacity-90 transition-all"
              >
                Back to all genies
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isLive = agent.is_active;
  const notifyOn = (agent.report_mode ?? (agent.send_conv_reports ? "every_conversation" : "off")) !== "off";
  const mascot = mascotForAgent(agent);
  const brandLogoUrl = resolveGenieAvatarUrl(agent);
  const tagline = agent.description || agent.category || "Front desk";

  return (
    <div className="flex flex-col h-screen w-full bg-genie-background overflow-hidden">
      <ImmersiveBackBar label="My Genie" to={`/genies/${id}/immersive-setup`} />

      <div className="flex-1 min-h-0 overflow-auto">
        <div className="flex flex-col items-center px-6 pt-7 pb-12">
          <div className="w-full max-w-[940px] flex flex-col gap-[18px]">
            {/* ── Header — genie identity + title ── */}
            <div className="flex flex-col gap-3.5">
              <div className="flex items-center gap-3">
                {brandLogoUrl ? (
                  <span
                    className="flex items-center justify-center rounded-full overflow-hidden flex-shrink-0"
                    style={{ width: 40, height: 40, backgroundColor: "#f3f4f6" }}
                  >
                    <img
                      src={brandLogoUrl}
                      alt={agent.name}
                      style={{ maxWidth: "80%", maxHeight: "80%", objectFit: "contain", display: "block" }}
                    />
                  </span>
                ) : (
                  <VoiceMascot mascot={mascot} size={32} rounded discSize={40} />
                )}
                <div className="min-w-0">
                  <p className="text-sm font-bold text-[#222B45] leading-tight truncate">
                    {agent.name}
                  </p>
                  <div className="flex items-center gap-1.5">
                    {isLive && (
                      <span className="w-1.5 h-1.5 rounded-full bg-genie-success ring-2 ring-genie-success/20" />
                    )}
                    <span className="text-[11.5px] text-gray-400 truncate">
                      {isLive ? "Live · " : ""}
                      {tagline}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h1 className="text-[26px] font-extrabold text-[#222B45] tracking-[-0.6px]">
                  Get your <i>Voice</i> genie out there
                </h1>
                <p className="text-sm text-[#64748B] mt-1">
                  Every way your customers can reach {agent.name}. Each one&rsquo;s ready in a click,
                  go live today.
                </p>
              </div>
            </div>

            {/* ── Hero share bar ── */}
            <DistributeShareBar
              url={shareLabel}
              copied={copied}
              onOpen={handleOpen}
              onCopy={handleCopy}
            />

            {/* ── Channels ── */}
            <p className="text-[11px] font-bold uppercase tracking-[0.8px] text-[#64748B] mt-0.5">
              Channels
            </p>

            <div className="flex flex-col gap-3.5">
              {/* Row 1 — Phone + Website embed */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 items-stretch">
                <DistributeChannelCard
                  genieIcon="phone"
                  tint="#FF4DB8"
                  title="Phone number"
                  description="A real number customers can call. Forward your existing line too."
                  status={phoneNumber ? "Live" : "Not added"}
                  statusLive={!!phoneNumber}
                  action={phoneNumber ? "Manage number" : "Add a number"}
                  actionPrimary={!phoneNumber}
                  onAction={() => setActiveModal("phone")}
                >
                  {phoneNumber && <DistributePhonePreview number={phoneNumber} />}
                </DistributeChannelCard>

                <DistributeChannelCard
                  genieIcon="code"
                  tint="#4E9CFF"
                  title="Website embed"
                  description="A chat + call widget on your site. Pick a style, paste one snippet."
                  status="Ready"
                  statusLive
                  action="Copy embed code"
                  actionPrimary
                  onAction={() => setActiveModal("webEmbed")}
                >
                  <DistributeEmbedPreview urlName={urlName ?? "your-genie"} />
                </DistributeChannelCard>
              </div>

              {/* Row 2 — QR + Help Genie app */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 items-stretch">
                <DistributeChannelCard
                  genieIcon="qr-code"
                  tint="#FF4DB8"
                  title="QR code"
                  description="Print it on a poster, flyer, table card or receipt. Scan to talk."
                  status={urlName ? "Ready" : "Not added"}
                  statusLive={!!urlName}
                  action="Download QR"
                  onAction={() => setActiveModal("qr")}
                >
                  <div className="flex items-center gap-3">
                    <QrGlyph value={shareUrl ?? undefined} size={64} className="flex-shrink-0" />
                    <p className="flex-1 text-[12px] text-gray-500 leading-snug">
                      Branded with your colours. PNG + SVG.
                    </p>
                  </div>
                </DistributeChannelCard>

                <DistributeChannelCard
                  genieIcon="genie-lamp"
                  tint="#4E9CFF"
                  title="Help Genie app"
                  description="Send a link that saves this genie into their app, ready whenever they need it."
                  status={keepUrl ? "Ready" : "Not added"}
                  statusLive={!!keepUrl}
                  action="Copy app link"
                  actionPrimary
                  onAction={handleCopyAppLink}
                >
                  <DistributeAppPreview genieName={agent.name} />
                </DistributeChannelCard>
              </div>

              {/* Row 3 — Email signature (full width) */}
              <DistributeChannelCard
                genieIcon="mail"
                tint="#FF4DB8"
                title="Email signature"
                description="A 'Talk to our genie' button in every email your team sends."
                status="Not added"
                action="Add to signature"
                onAction={() => setActiveModal("share")}
              >
                <DistributeSignaturePreview owner={ownerName} />
              </DistributeChannelCard>

              {/* Row 4 — Notifications (full width) */}
              <DistributeNotificationsRow
                enabled={notifyOn}
                onToggle={() =>
                  updateAgent.mutate({
                    agent,
                    updateData: {
                      send_conv_reports: !notifyOn,
                      report_mode: !notifyOn ? "every_conversation" : "off",
                    },
                  })
                }
              />
            </div>

            {/* ── Activation pack ── */}
            <DistributeActivationPack onOpen={handleActivationPack} />
          </div>
        </div>
      </div>

      {/* ── REAL distribution modals (side sheets) ── */}
      <PhoneNumberModal genie={agent} isOpen={activeModal === "phone"} onClose={close} />
      <WebEmbedModal genie={agent} isOpen={activeModal === "webEmbed"} onClose={close} />
      <QRCodeModal genie={agent} isOpen={activeModal === "qr"} onClose={close} />
      <GenieShareDrawer
        genie={agent}
        isOpen={activeModal === "share"}
        onClose={close}
        onOpenPhoneModal={() => setActiveModal("phone")}
      />
    </div>
  );
};

export default DistributePage;
